import { useCallback, useEffect, useRef, useState } from 'react';
import { createEars } from './ears';
import { createMouth } from './mouth';
import { createReplySpeaker } from './replySpeaker';
import { playEarcon as realEarcon } from './earcon';
import { createFillers, FILLER_AGAIN_MS, FILLER_DELAY_MS, MAX_FILLERS } from './fillers';
import { languageOf } from './languageOf';
import { useSharedAudio } from './sharedAudio';
import { errorText, voiceLanguage, voiceText } from './voiceStrings';

/**
 * VOICE MODE: a hands-free loop the person never has to drive.
 *
 *   listen → live words → end of speech → send(text, language) → thinking → speak the reply → listen
 *
 * The microphone is closed while she thinks and speaks (no echo loop). A tap while she speaks interrupts her. Mute,
 * Stop and Type are always there. Every failure is one plain line in the person's language.
 *
 *   const v = useVoiceConversation({ language, send, transcribe, synthesize, getLiveSession, haptic })
 *   v.start()                 // inside the tap: opens the microphone, unlocks sound
 *   v.stop()  v.mute()  v.unmute()  v.interrupt()  v.retry()
 *   v.active  v.state  v.label  v.words  v.level  v.error  v.muted
 *   v.state is 'off' | 'connecting' | 'listening' | 'hearing' | 'thinking' | 'speaking' | 'muted' | 'failed'
 *
 * Transports (the app passes them; this module knows no axios, no redux):
 *   send(text, language)            → the chat turn. Resolves when the answer is on screen, with what to speak:
 *                                     { key, text, language } | { key, replyId, language } (anything the app's
 *                                     synthesize takes), or a string (spoken in the language of its own script), or null.
 *   synthesize({ ...speak, signal })→ a fetch-style Response (see mouth.js); ask for format 'pcm'.
 *   transcribe(blob)                → { text, language } (the clip door); used by the clip ears.
 *   getLiveSession()                → { token, wsUrl } (the owner's live door). Omit for clip ears only (customers).
 *   haptic(kind)                    → 'start' | 'send' | 'lock' | 'cancel'
 * `language` is the UI language (labels, errors) — never the language of the reply, which follows the voice.
 */
const DEAD_EARS = new Set(['mic-blocked', 'unsupported', 'session', 'socket', 'closed', 'idle']);

export function useVoiceConversation({ language = 'en', send, transcribe, synthesize, getLiveSession, haptic, deps = {} } = {}) {
  useSharedAudio();
  const [state, setState] = useState('off');
  const [words, setWords] = useState('');
  const [caption, setCaption] = useState(''); // what Luna has begun to say, in step with the sound
  const fillers = useRef(null);
  const fillerTimer = useRef(null);
  const fillerHandle = useRef(null);
  const speaker = useRef(null);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState('');
  const run = useRef(0);
  const ears = useRef(null);
  const mouth = useRef(null);
  const lang = useRef(language);
  lang.current = language;
  const props = useRef({});
  props.current = { send, transcribe, synthesize, getLiveSession, haptic };
  const stateRef = useRef('off');
  const inTurn = useRef(false); // one send per turn: a late second 'completed' is ignored
  const earsDead = useRef(false); // the ears' own error says they are gone (closed, failed to open, let go for silence)
  const set = useCallback((s) => { stateRef.current = s; setState(s); }, []);

  // The filler (a slow turn) ends the moment the real answer's sound starts, or the turn ends any other way.
  const endFiller = useCallback(() => {
    if (fillerTimer.current) { clearTimeout(fillerTimer.current); fillerTimer.current = null; }
    if (fillerHandle.current) { try { fillerHandle.current.stop(); } catch (_) { /* over */ } fillerHandle.current = null; }
  }, []);

  const closeAll = useCallback(() => {
    run.current += 1;
    try { ears.current?.close(); } catch (_) { /* gone */ }
    ears.current = null;
    try { mouth.current?.stop(); } catch (_) { /* gone */ }
    endFiller();
  }, [endFiller]);
  useEffect(() => closeAll, [closeAll]);

  const listen = useCallback(() => {
    setWords('');
    ears.current?.resume();
    set('listening');
  }, [set]);

  const fail = useCallback((code, line) => {
    ears.current?.pause();
    setError(line || errorText(lang.current, code));
    set('failed');
  }, [set]);

  const takeTurn = useCallback(async (mine, text, said) => {
    inTurn.current = true;
    try {
      ears.current?.pause();
      set('thinking');
      setWords(''); // sent: the words are the person's bubble now
      setCaption('');
      props.current.haptic?.('send');
      const muted = () => stateRef.current === 'muted';
      // Acknowledged at once: a soft earcon (silenced by Mute), and a filler only if the turn turns out slow, in ITS language.
      if (!muted()) (deps.playEarcon || realEarcon)();
      const fl = fillers.current;
      const fillerLang = ['he', 'ar', 'en'].includes(said) ? said : null;
      if (fl && fillerLang) {
        fl.prepare?.(fillerLang);
        // A turn that stays silent gets another filler every FILLER_AGAIN_MS (up to MAX_FILLERS): never 30 s of nothing.
        const arm = (delay, n) => {
          fillerTimer.current = setTimeout(() => {
            fillerTimer.current = null;
            if (run.current !== mine || muted() || stateRef.current !== 'thinking') return;
            fillerHandle.current = fl.play(fillerLang);
            if (n + 1 < MAX_FILLERS) arm(FILLER_AGAIN_MS, n + 1);
          }, delay);
        };
        arm(FILLER_DELAY_MS, 0);
      }
      // First words fast: sentences are spoken as the reply streams in.
      const sp = createReplySpeaker({ mouth: mouth.current, key: `turn-${mine}-${text.length}`, language: said, onReveal: (t) => { if (run.current === mine) setCaption(t); } });
      speaker.current = sp;
      let reply;
      try {
        reply = await props.current.send(text, said, { onDelta: (d) => { if (run.current === mine && !muted()) sp.push(d); } });
      } catch (e) {
        sp.cancel(); endFiller();
        if (run.current === mine) fail('failed');
        return;
      }
      if (run.current !== mine) { sp.cancel(); return; }
      // Muted while she thought: the reply is on screen, nothing is spoken and the microphone stays closed.
      if (muted()) { sp.cancel(); endFiller(); return; }
      const speech = typeof reply === 'string' ? (reply.trim() ? { key: reply, text: reply, language: languageOf(reply) || said } : null) : reply;
      if (speech && (speech.text || speech.replyId)) {
        // The state says "speaking" when the SOUND starts (the mouth's onState), not when the words are in hand: the synthesis takes
        // ~0.6 s and "Speaking" over silence is a status that is not true (28 Oct, measured on the sound itself).
        let how;
        if (speech.text) {
          // A card to approve is on screen: the spoken reply says so, and approving is a tap (never a spoken yes).
          const line = speech.confirm ? voiceText(speech.language || said || lang.current, 'approveOnScreen') : '';
          const full = line ? `${String(speech.text).trim()} ${line}` : String(speech.text);
          how = await sp.end(full, speech.key, speech.language);
        } else {
          how = await speakById(mouth.current, speech);
        }
        endFiller();
        if (run.current !== mine) return;
        if (stateRef.current === 'muted') return; // muted while she spoke: stay muted, the microphone is not resumed
        if (how === 'failed') { /* the reply stays on screen; listening goes on */ }
      } else {
        // Cards only (or a draft that was spoken and no final): whatever began to be said is finished, then listening resumes.
        await sp.end('', null);
        endFiller();
      }
      if (run.current !== mine) return;
      if (stateRef.current === 'muted') return;
      listen();
    } finally {
      endFiller();
      if (run.current === mine) inTurn.current = false;
    }
  }, [deps, endFiller, fail, listen, set]);

  const start = useCallback(() => {
    if (stateRef.current !== 'off' && stateRef.current !== 'failed') return;
    closeAll();
    const mine = run.current;
    inTurn.current = false; earsDead.current = false;
    setError(''); setWords(''); setLevel(0);
    set('connecting');
    props.current.haptic?.('start');
    mouth.current = (deps.createMouth || createMouth)({
      synthesize: (a) => props.current.synthesize(a),
      // The real answer's sound has started: the filler is cut that instant, and the state is speaking.
      onState: (s) => { if (run.current !== mine || s !== 'speaking') return; endFiller(); if (stateRef.current === 'thinking') set('speaking'); },
    });
    mouth.current.unlock();
    fillers.current = deps.fillers || fillers.current || createFillers();
    fillers.current.prepare?.(voiceLanguage(lang.current));
    const opts = {
      kind: 'conversation', continuous: true,
      getLiveSession: props.current.getLiveSession ? () => props.current.getLiveSession() : undefined,
      transcribe: (b) => props.current.transcribe(b),
      onPartial: (t) => { if (run.current === mine && stateRef.current !== 'editing') setWords(t); },
      onLevel: (l) => { if (run.current === mine) setLevel(l); },
      onState: (s) => {
        if (run.current !== mine) return;
        if (s === 'listening' && ['connecting', 'listening'].includes(stateRef.current)) set('listening');
        else if (s === 'hearing' && ['connecting', 'listening', 'hearing'].includes(stateRef.current)) set('hearing');
      },
      onFinal: ({ text, language: said } = {}) => {
        if (run.current !== mine) return;
        if (stateRef.current === 'editing') { if (text) setWords(text); return; } // the person is fixing the words: nothing is sent
        if (inTurn.current || !['connecting', 'listening', 'hearing'].includes(stateRef.current)) return; // a late second 'completed'
        if (!text) { setWords(''); fail('not-heard', ''); setError(''); return; }
        setWords(text);
        takeTurn(mine, text, said || voiceLanguage(lang.current));
      },
      onError: (e) => { if (run.current !== mine) return; if (DEAD_EARS.has(e?.code)) earsDead.current = true; fail(e?.code); },
      deps: deps.ears,
    };
    ears.current = (deps.startEars || createEars)(opts);
    ears.current.ready?.catch?.(() => {});
  }, [closeAll, deps, endFiller, fail, set, takeTurn]);

  const stop = useCallback(() => { closeAll(); setWords(''); setLevel(0); setError(''); set('off'); }, [closeAll, set]);
  const mute = useCallback(() => { if (stateRef.current === 'off') return; try { mouth.current?.stop(); } catch (_) { /* gone */ } ears.current?.pause(); set('muted'); }, [set]);
  const unmute = useCallback(() => {
    if (stateRef.current !== 'muted') return;
    if (inTurn.current) set('thinking'); // her turn is still going: it will speak or listen when it is done
    else listen();
  }, [listen, set]);
  const interrupt = useCallback(() => { if (stateRef.current === 'speaking') { mouth.current?.stop(); } }, []);
  const retry = useCallback(() => {
    if (stateRef.current !== 'failed') return;
    // Ears that are closed or failed cannot listen again: a fresh pair (start) — inside the tap that called retry().
    if (earsDead.current || !ears.current) { start(); return; }
    setError('');
    listen();
  }, [listen, start]);

  // Tap Luna: while she hears you, send now; while she speaks, interrupt; when it failed, try again.
  const sendNow = useCallback(() => {
    if (stateRef.current !== 'hearing') return;
    try { ears.current?.finish?.(); } catch (_) { /* gone */ }
  }, []);
  const tapFigure = useCallback(() => {
    const s = stateRef.current;
    if (s === 'hearing') sendNow();
    else if (s === 'speaking') { mouth.current?.stop(); }
    else if (s === 'failed') retry();
  }, [retry, sendNow]);

  // The heard words can be fixed by typing before they are sent: the turn is held, the ears are closed.
  const beginEdit = useCallback(() => {
    if (!['hearing', 'listening'].includes(stateRef.current)) return;
    ears.current?.pause();
    set('editing');
  }, [set]);
  const cancelEdit = useCallback(() => { if (stateRef.current === 'editing') listen(); }, [listen]);
  const submitEdit = useCallback((typed) => {
    if (stateRef.current !== 'editing') return;
    const t = String(typed ?? '').trim();
    if (!t) { listen(); return; }
    takeTurn(run.current, t, languageOf(t) || voiceLanguage(lang.current));
  }, [listen, takeTurn]);

  const key = state === 'failed' ? 'couldntHear' : state === 'off' ? 'listening' : state === 'editing' ? 'hearing' : state;
  return {
    active: state !== 'off', state, words, caption, level, error, muted: state === 'muted',
    label: voiceText(language, key),
    start, stop, mute, unmute, interrupt, retry, sendNow, tapFigure, beginEdit, cancelEdit, submitEdit,
  };
}

/**
 * A reply the SERVER holds (the customer's replyId): it is spoken part by part. Part 0 answers with how many parts there
 * are (X-Speech-Parts) and the rest are queued behind it, in order, one synthesis each. An older server that ignores `part`
 * speaks it whole and sends no header: one part.
 */
async function speakById(mouth, speech) {
  const base = speech.key ?? speech.replyId;
  const results = [];
  results.push(mouth.speak({ ...speech, key: `${base}#0`, part: 0 }, {
    onResponse: (res) => {
      const n = Math.min(12, parseInt(res?.headers?.get?.('x-speech-parts'), 10) || 1);
      for (let i = 1; i < n; i++) results.push(mouth.enqueue({ ...speech, key: `${base}#${i}`, part: i }));
    },
  }));
  let first = await results[0];
  const all = [first, ...(await Promise.all(results.slice(1)))];
  if (all.includes('stopped')) return 'stopped';
  return all.every((r) => r === 'failed') ? 'failed' : 'done';
}
